import React, { useEffect } from 'react';
import styled, { createGlobalStyle } from 'styled-components';
import { Link } from 'react-router-dom';
import { FaArrowRight } from 'react-icons/fa';

/* ─── GLOBAL ─────────────────────────────────────────────────────── */
const GlobalStyle = createGlobalStyle`
  * { box-sizing: border-box; margin: 0; padding: 0; }
`;

/* ─── TOKENS ─────────────────────────────────────────────────────── */
const T = {
  navy:    '#1a1a2e',
  indigo:  '#3730A3',
  deep:    '#0b0b77',
  lavender:'#a78bfa',
  cream:   '#faf9f7',
  charcoal:'#4a4a6a',
  muted:   '#9090b0',
  rule:    'rgba(55,48,163,0.18)',
  serif:   `'Times New Roman', Georgia, serif`,
};

/* ─── PAGE WRAPPER ───────────────────────────────────────────────── */
const PageContainer = styled.div`
  margin-top: 195px;
  font-family: ${T.serif};
  color: ${T.navy};
  background: #fff;

  @media (max-width: 1366px) and (min-width: 769px) {
    margin-top: 158px;
  }


  @media (max-width: 768px) {
    margin-top: 120px;
  }
`;

/* ═══════════════════════════════════════════════════════════════════
   HERO
═══════════════════════════════════════════════════════════════════ */
const HeroSection = styled.section`
  background: linear-gradient(160deg, #1a1070 0%, #252080 30%, #3730A3 60%, #1a1070 100%);
  color: #fff;
  padding: 2.5rem 5% 2rem;
  position: relative;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    left: 0;
    top: 0;
    bottom: 0;
    width: 5px;
    background: linear-gradient(180deg, ${T.lavender} 0%, ${T.indigo} 100%);
  }

  &::after {
    content: '';
    position: absolute;
    right: -60px;
    top: -60px;
    width: 350px;
    height: 350px;
    background: radial-gradient(circle, rgba(167, 139, 250, 0.06) 0%, transparent 70%);
    border-radius: 50%;
    pointer-events: none;
  }
`;

const HeroEyebrow = styled.div`
  font-family: ${T.serif};
  font-size: 0.72rem;
  letter-spacing: 0.2em;
  text-transform: uppercase;
  color: ${T.lavender};
  margin-bottom: 0.8rem;
  font-weight: 700;
`;

const HeroTitle = styled.h1`
  font-family: ${T.serif};
  font-size: clamp(2.4rem, 4.5vw, 3.4rem);
  font-weight: 900;
  line-height: 1.1;
  padding-top: 0.4rem;
  letter-spacing: -0.02em;

  strong {
    color: ${T.lavender};
  }
`;

const HeroRule = styled.div`
  height: 1px;
  background: rgba(255,255,255,0.15);
  margin: 0.8rem 0;
`;

const HeroDesc = styled.p`
  font-family: ${T.serif};
  font-size: 1rem;
  color: rgba(255,255,255,0.75);
  max-width: 560px;
  line-height: 1.7;
`;

const HeroWatermark = styled.div`
  position: absolute;
  top: 2rem;
  right: 5%;
  font-size: 5rem;
  font-weight: 900;
  color: rgba(167,139,250,0.08);
  line-height: 1;
  letter-spacing: -0.03em;
  pointer-events: none;
  user-select: none;

  @media (max-width: 600px) { display: none; }
`;

/* ═══════════════════════════════════════════════════════════════════
   TICKER BAND
═══════════════════════════════════════════════════════════════════ */
const Ticker = styled.div`
  display: flex;
  background: ${T.cream};
  border-bottom: 1px solid ${T.rule};

  @media (max-width: 600px) { flex-wrap: wrap; }
`;

const TickerItem = styled.div`
  flex: 1;
  min-width: 25%;
  padding: 1.4rem 1.5rem;
  border-right: 1px solid ${T.rule};
  text-align: center;

  &:last-child { border-right: none; }

  @media (max-width: 600px) {
    min-width: 50%;
    &:nth-child(2) { border-right: none; }
  }
`;

const TickerVal = styled.div`
  font-family: ${T.serif};
  font-size: 2.5rem;
  font-weight: 900;
  color: ${T.indigo};
  letter-spacing: -0.02em;
  line-height: 1;

  em {
    font-size: 1.3rem;
    font-style: normal;
    color: ${T.lavender};
  }
`;

const TickerLabel = styled.div`
  font-family: ${T.serif};
  font-size: 0.72rem;
  letter-spacing: 0.15em;
  text-transform: uppercase;
  color: ${T.muted};
  margin-top: 0.3rem;
  font-weight: 700;
`;

/* ═══════════════════════════════════════════════════════════════════
   VALUES BAND
═══════════════════════════════════════════════════════════════════ */
const ValuesBand = styled.div`
  padding: 2.5rem 5%;
  background: ${T.cream};
  border-top: 1px solid ${T.rule};
  border-bottom: 1px solid ${T.rule};
`;

const ValuesInner = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 2rem;

  @media (max-width: 600px) { grid-template-columns: 1fr; }
`;

const ValueItem = styled.div`
  padding-right: 1rem;
`;

const ValueNum = styled.div`
  font-family: ${T.serif};
  font-size: 2.8rem;
  color: ${T.rule};
  line-height: 1;
  font-weight: 900;
`;

const ValueTitle = styled.div`
  font-family: ${T.serif};
  font-size: 1rem;
  font-weight: 700;
  color: ${T.indigo};
  margin-bottom: 0.4rem;
  border-bottom: 1px solid ${T.rule};
  padding-bottom: 0.4rem;
`;

const ValueDesc = styled.p`
  font-family: ${T.serif};
  font-size: 0.9rem;
  color: ${T.charcoal};
  line-height: 1.8;
`;

/* ═══════════════════════════════════════════════════════════════════
   EDITORIAL BODY — TWO COLUMNS
═══════════════════════════════════════════════════════════════════ */
const BodyGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;

  @media (max-width: 720px) { grid-template-columns: 1fr; }
`;

/* ── Left column ── */
const LeftCol = styled.div`
  padding: 3rem 5%;
  border-right: 1px solid ${T.rule};

  @media (max-width: 720px) {
    border-right: none;
    border-bottom: 1px solid ${T.rule};
  }
`;

const SectionEyebrow = styled.div`
  font-family: ${T.serif};
  font-size: 0.72rem;
  letter-spacing: 0.2em;
  text-transform: uppercase;
  color: ${T.indigo};
  margin-bottom: 0.5rem;
  font-weight: 700;
`;

const SectionTitle = styled.h2`
  font-family: ${T.serif};
  font-size: 1.9rem;
  font-weight: 800;
  color: ${T.navy};
  margin-bottom: 1.5rem;
  line-height: 1.2;
`;

const StoryText = styled.p`
  font-family: ${T.serif};
  font-size: 1rem;
  color: ${T.charcoal};
  line-height: 1.9;
  margin-bottom: 1.2rem;
`;

const PullQuote = styled.blockquote`
  border-left: 3px solid ${T.lavender};
  padding: 0.8rem 1.2rem;
  margin: 1.5rem 0;
  background: rgba(167,139,250,0.06);

  p {
    font-family: ${T.serif};
    font-size: 1.15rem;
    font-style: italic;
    color: ${T.navy};
    line-height: 1.6;
  }

  cite {
    display: block;
    margin-top: 0.5rem;
    font-size: 0.8rem;
    letter-spacing: 0.1em;
    text-transform: uppercase;
    color: ${T.indigo};
    font-style: normal;
    font-weight: 700;
  }
`;

const MissionBand = styled.div`
  background: ${T.indigo};
  color: #fff;
  padding: 1.8rem;
  margin-top: 1.8rem;
  position: relative;
  overflow: hidden;

  &::after {
    content: '"';
    position: absolute;
    right: -0.2rem;
    bottom: -1.5rem;
    font-size: 8rem;
    color: rgba(167,139,250,0.2);
    line-height: 1;
    pointer-events: none;
  }

  h3 {
    font-family: ${T.serif};
    font-size: 0.72rem;
    letter-spacing: 0.2em;
    text-transform: uppercase;
    color: ${T.lavender};
    margin-bottom: 0.6rem;
    font-weight: 700;
  }

  p {
    font-family: ${T.serif};
    font-size: 1.05rem;
    font-style: italic;
    line-height: 1.7;
    color: rgba(255,255,255,0.9);
  }
`;

/* ── Right column — Legacy Wall ── */
const RightCol = styled.div`
  padding: 3rem 5%;
  background: #fdfcfc;

  @media (max-width: 720px) { background: #fff; }
`;

const LegacyWall = styled.div`
  display: flex;
  flex-direction: column;
`;

const Milestone = styled.div`
  display: grid;
  grid-template-columns: 80px 1fr;
  gap: 0 1.2rem;
  padding: 1.4rem 0;
  border-bottom: 1px solid ${T.rule};
  position: relative;

  &:last-child { border-bottom: none; }
`;

const MilestoneYear = styled.div`
  font-family: ${T.serif};
  font-size: 1.8rem;
  font-weight: 900;
  color: ${T.indigo};
  letter-spacing: -0.03em;
  line-height: 1;
  padding-top: 0.15rem;
  text-align: right;
  padding-right: 1.2rem;
  position: relative;

  &::after {
    content: '';
    position: absolute;
    top: 0.6rem;
    right: 0;
    width: 7px;
    height: 7px;
    border-radius: 50%;
    background: ${T.lavender};
  }
`;

const MilestoneLine = styled.div`
  position: absolute;
  left: 79px;
  top: 1.7rem;
  bottom: 0;
  width: 1px;
  background: ${T.rule};

  ${Milestone}:last-child & { display: none; }
`;

const MilestoneText = styled.div`
  font-family: ${T.serif};
  font-size: 0.95rem;
  color: ${T.charcoal};
  line-height: 1.8;
`;

/* ═══════════════════════════════════════════════════════════════════
   CTA STRIP
═══════════════════════════════════════════════════════════════════ */
const CTAStrip = styled.div`
  background: ${T.navy};
  padding: 2.5rem 5%;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  flex-wrap: wrap;

  @media (max-width: 600px) {
    flex-direction: column;
    text-align: center;
  }
`;

const CTAText = styled.div`
  h3 {
    font-family: ${T.serif};
    font-size: 1.5rem;
    font-weight: 800;
    color: #fff;
    margin-bottom: 0.2rem;
  }
  p {
    font-family: ${T.serif};
    font-size: 0.9rem;
    color: rgba(255,255,255,0.6);
  }
`;

const CTAButton = styled(Link)`
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  background: ${T.lavender};
  border: none;
  color: ${T.navy};
  padding: 0.8rem 2rem;
  font-size: 0.95rem;
  font-family: ${T.serif};
  text-decoration: none;
  letter-spacing: 0.05em;
  white-space: nowrap;
  font-weight: 700;
  border-radius: 4px;
  transition: background 0.2s, transform 0.2s;

  &:hover {
    background: #c4b5fd;
    transform: translateY(-2px);
  }
`;

/* ═══════════════════════════════════════════════════════════════════
   DATA
═══════════════════════════════════════════════════════════════════ */
const stats = [
  { val: '2008',  suffix: '',  label: 'Year Founded' },
  { val: '5000',  suffix: '+', label: 'Students Trained' },
  { val: '98',    suffix: '%', label: 'Success Rate' },
  { val: '17',    suffix: '+', label: 'Years of Excellence' },
];

const values = [
  {
    num: 'I.',
    title: 'Expert-Led Learning',
    desc: 'Every programme is built and delivered by working professionals with decades of hands-on industry experience — not just theory.',
  },
  {
    num: 'II.',
    title: 'Proven Methodology',
    desc: 'Our unique LearnThruUs approach combines structured curriculum, practical exercises, and continuous assessment to ensure mastery of every concept.',
  },
  {
    num: 'III.',
    title: 'Lifelong Community',
    desc: 'Joining Learn Thru Us means joining a network of over 5,000 alumni, mentors, and peers who continue to support each other long after graduation.',
  },
];

const milestones = [
  {
    year: '2008',
    text: 'Learn Thru Us Academy founded in Hyderabad with a mission to provide high-quality medical coding training to aspiring healthcare professionals across India.',
  },
  {
    year: '2012',
    text: 'Introduced specialised medical coding modules that attracted students from diverse healthcare backgrounds, establishing a reputation for rigorous, role-ready training.',
  },
  {
    year: '2015',
    text: 'Launched comprehensive online training programmes — making quality medical coding education accessible to learners across every corner of India.',
  },
  {
    year: '2018',
    text: 'Reached the 2,000+ students milestone, maintaining a remarkable 98% certification pass rate — a benchmark that continues to define our standard of excellence.',
  },
  {
    year: '2020',
    text: 'Introduced advanced specialty coding programmes — including CDI, HCC, and Inpatient DRG training — to meet the evolving demands of the global healthcare market.',
  },
  {
    year: '2024',
    text: 'Celebrated 5,000+ successful graduates placed globally across the US, Middle East, and India — a milestone that reflects the trust of students and the strength of our curriculum.',
  },
];

/* ═══════════════════════════════════════════════════════════════════
   COMPONENT
═══════════════════════════════════════════════════════════════════ */
const AboutUs = () => {
  useEffect(() => { window.scrollTo(0, 0); }, []);

  return (
    <>
      <GlobalStyle />
      <PageContainer>

        {/* ── HERO ── */}
        <HeroSection>
          <HeroEyebrow>Since 2008 · Hyderabad, India</HeroEyebrow>
          <HeroTitle>
            About <strong>Learn Thru Us</strong><br />Academy
          </HeroTitle>
          <HeroRule />
          <HeroDesc>
            Empowering healthcare professionals with industry-leading coding and
            documentation training for over 17+ years — and counting.
          </HeroDesc>
          <HeroWatermark aria-hidden="true">2008</HeroWatermark>
        </HeroSection>

        {/* ── TICKER ── */}
        <Ticker>
          {stats.map((s, i) => (
            <TickerItem key={i}>
              <TickerVal>
                {s.val}{s.suffix && <em>{s.suffix}</em>}
              </TickerVal>
              <TickerLabel>{s.label}</TickerLabel>
            </TickerItem>
          ))}
        </Ticker>

        {/* ── VALUES ── */}
        <ValuesBand>
          <ValuesInner>
            {values.map((v, i) => (
              <ValueItem key={i}>
                <ValueNum>{v.num}</ValueNum>
                <ValueTitle>{v.title}</ValueTitle>
                <ValueDesc>{v.desc}</ValueDesc>
              </ValueItem>
            ))}
          </ValuesInner>
        </ValuesBand>

        {/* ── EDITORIAL BODY ── */}
        <BodyGrid>

          {/* LEFT: Story */}
          <LeftCol>
            <SectionEyebrow>Our Story</SectionEyebrow>
            <SectionTitle>
              A small classroom.<br />A nation-wide legacy.
            </SectionTitle>

            <StoryText>
              Learn Thru Us Academy was founded in 2008 with a simple, unwavering
              mission: to provide high-quality, accessible training for healthcare
              professionals across India. What started as a small classroom in
              Hyderabad has grown into one of the country's most trusted names in
              medical coding education.
            </StoryText>
            <StoryText>
              Our founder, <strong>Gaayatri Jayapal</strong>, recognised the growing need for
              skilled medical coders in the rapidly expanding healthcare industry.
              Drawing on over two decades of firsthand industry experience, she
              developed the unique <strong>LearnThruUs Methodology</strong> — a structured,
              outcome-driven approach that has guided thousands of students to their
              career goals and first-attempt certification success.
            </StoryText>
            <StoryText>
              Today, our graduates work in leading hospitals, insurance giants, and
              global healthcare firms across three continents. We remain a Hyderabad
              institution at heart, with a reach that has grown far beyond what that
              first classroom could have imagined.
            </StoryText>

            <PullQuote>
              <p>"Your success is our mission. We don't just teach medical coding — we build careers."</p>
              <cite>— Gaayatri Jayapal, Founder</cite>
            </PullQuote>

            <MissionBand>
              <h3>Our Mission</h3>
              <p>
                To bridge the gap between academic knowledge and industry demand,
                equipping every student with the skills, confidence, and credentials
                to thrive in a competitive healthcare landscape — wherever in the
                world they choose to work.
              </p>
            </MissionBand>
          </LeftCol>

          {/* RIGHT: Legacy Wall */}
          <RightCol>
            <SectionEyebrow>Our Journey</SectionEyebrow>
            <SectionTitle>
              Milestones that<br />shaped us.
            </SectionTitle>

            <LegacyWall>
              {milestones.map((m, i) => (
                <Milestone key={i}>
                  <MilestoneYear>{m.year}</MilestoneYear>
                  <MilestoneLine />
                  <MilestoneText>{m.text}</MilestoneText>
                </Milestone>
              ))}
            </LegacyWall>
          </RightCol>

        </BodyGrid>

        {/* ── CTA ── */}
        <CTAStrip>
          <CTAText>
            <h3>Join our success story.</h3>
            <p>Become part of the Learn Thru Us family and transform your career today.</p>
          </CTAText>
          <CTAButton to="/all-courses">
            Explore Courses <FaArrowRight />
          </CTAButton>
        </CTAStrip>

      </PageContainer>
    </>
  );
};

export default AboutUs;